import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  Download, Upload, FileJson, AlertTriangle, CheckCircle,
  Database, FileText, GraduationCap, BookOpen, Compass,
  Smartphone, Globe, Video, BarChart3, Loader2, Users, Key
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ENTITIES = [
  { key: 'courses', label: 'Courses', icon: GraduationCap, color: '#00d4ff' },
  { key: 'books', label: 'Books', icon: BookOpen, color: '#ffd700' },
  { key: 'guides', label: 'Guides', icon: Compass, color: '#a855f7' },
  { key: 'apps', label: 'Apps', icon: Smartphone, color: '#ec4899' },
  { key: 'websites', label: 'Websites', icon: Globe, color: '#22c55e' },
  { key: 'channels', label: 'Channels', icon: Video, color: '#ff0000' },
  { key: 'gallery', label: 'Gallery', icon: BookOpen, color: '#e879f9' },
  { key: 'stats', label: 'Stats', icon: BarChart3, color: '#f59e0b' },
  { key: 'users', label: 'Users', icon: Users, color: '#60a5fa' },
  { key: 'user_sessions', label: 'Sessions', icon: Key, color: '#f472b6' },
];

const MODES = [
  { value: 'replace', label: 'Replace', desc: 'Wipe existing data and replace with imported' },
  { value: 'upsert', label: 'Upsert', desc: 'Update by ID if exists, insert if new (safest for partial updates)' },
  { value: 'append', label: 'Append', desc: 'Add all as new entries (ignores IDs)' },
];

const downloadJSON = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const AdminDataManager = () => {
  const [exporting, setExporting] = useState(null);
  const [importEntity, setImportEntity] = useState('books');
  const [importMode, setImportMode] = useState('upsert');
  const [importFile, setImportFile] = useState(null);
  const [importPayload, setImportPayload] = useState(null); // parsed JSON
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [isBundle, setIsBundle] = useState(false);
  const fileInputRef = useRef(null);

  const handleExportOne = async (entity) => {
    setExporting(entity);
    try {
      const res = await axios.get(`${API}/admin/export/${entity}`, { withCredentials: true });
      downloadJSON(res.data, `inner-bridge-${entity}-${Date.now()}.json`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Export failed');
    }
    setExporting(null);
  };

  const handleExportAll = async () => {
    setExporting('all');
    try {
      const res = await axios.get(`${API}/admin/export`, { withCredentials: true });
      downloadJSON(res.data, `inner-bridge-full-backup-${Date.now()}.json`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Export failed');
    }
    setExporting(null);
  };

  const parseFile = (file) => {
    setError('');
    setPreview(null);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        // Accept either {entity, data} single-entity export, {data: {...}} full bundle, or raw array
        if (parsed.data && typeof parsed.data === 'object' && !Array.isArray(parsed.data) && ENTITIES.some(x => x.key in parsed.data)) {
          // Full bundle
          setImportPayload(parsed.data);
          setIsBundle(true);
        } else if (parsed.entity && 'data' in parsed) {
          // Single-entity export wrapper
          setImportEntity(parsed.entity);
          setImportPayload(parsed.data);
          setIsBundle(false);
        } else if (Array.isArray(parsed)) {
          // Raw array
          setImportPayload(parsed);
          setIsBundle(false);
        } else {
          setError('Unrecognized JSON format. Expected {entity, data} or full bundle {data: {...}} or raw array.');
          setImportFile(null);
        }
      } catch (err) {
        setError('Invalid JSON: ' + err.message);
        setImportFile(null);
      }
    };
    reader.readAsText(file);
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      setError('Only .json files are supported');
      return;
    }
    setImportFile(file);
    parseFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    setPreview(null);
    setResult(null);
    try {
      const url = isBundle ? `${API}/admin/import-all/preview` : `${API}/admin/import/preview`;
      const body = isBundle
        ? { mode: importMode, data: importPayload }
        : { entity: importEntity, mode: importMode, data: importPayload };
      const res = await axios.post(url, body, { withCredentials: true });
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Preview failed');
    }
    setLoading(false);
  };

  const handleApply = async () => {
    if (!window.confirm(`Apply import in "${importMode}" mode? This will modify your database.`)) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const url = isBundle ? `${API}/admin/import-all` : `${API}/admin/import`;
      const body = isBundle
        ? { mode: importMode, data: importPayload }
        : { entity: importEntity, mode: importMode, data: importPayload };
      const res = await axios.post(url, body, { withCredentials: true });
      setResult(res.data);
      setPreview(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Import failed');
    }
    setLoading(false);
  };

  const resetImport = () => {
    setImportFile(null);
    setImportPayload(null);
    setPreview(null);
    setResult(null);
    setError('');
    setIsBundle(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div style={{ color: '#e0dff0', padding: '0 4px' }} data-testid="admin-data-manager">
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Database size={26} color="#a855f7" /> Data Manager
        </h1>
        <p style={{ color: '#8885a0', fontSize: 14 }}>
          Export content as JSON for backup, or import JSON files to restore or migrate data.
        </p>
      </div>

      {/* EXPORT SECTION */}
      <div style={cardStyle}>
        <div style={sectionHeader}>
          <Download size={18} color="#00d4ff" />
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>Export</h2>
        </div>

        <button
          data-testid="export-all-btn"
          onClick={handleExportAll}
          disabled={exporting === 'all'}
          style={{
            ...primaryBtn,
            width: '100%',
            marginBottom: 16,
            background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
            opacity: exporting === 'all' ? 0.6 : 1,
          }}
        >
          {exporting === 'all'
            ? (<><Loader2 size={16} className="animate-spin" /> Preparing full backup...</>)
            : (<><FileJson size={16} /> Export Full Backup (all collections)</>)}
        </button>

        <p style={{ fontSize: 12, color: '#8885a0', marginBottom: 12 }}>
          Or export a single collection:
        </p>

        <div style={gridStyle}>
          {ENTITIES.map(({ key, label, icon: Icon, color }) => (
            <button
              key={key}
              data-testid={`export-${key}-btn`}
              onClick={() => handleExportOne(key)}
              disabled={exporting === key}
              style={{
                ...outlineBtn,
                borderColor: `${color}40`,
                opacity: exporting === key ? 0.6 : 1,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${color}40`; }}
            >
              {exporting === key
                ? <Loader2 size={16} className="animate-spin" />
                : <Icon size={16} color={color} />}
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* IMPORT SECTION */}
      <div style={{ ...cardStyle, marginTop: 20 }}>
        <div style={sectionHeader}>
          <Upload size={18} color="#ffd700" />
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>Import</h2>
        </div>

        {/* File drop zone */}
        {!importFile && (
          <div
            data-testid="import-dropzone"
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragActive ? '#a855f7' : 'rgba(139, 92, 246, 0.3)'}`,
              borderRadius: 12,
              padding: '40px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: dragActive ? 'rgba(139, 92, 246, 0.08)' : 'rgba(139, 92, 246, 0.03)',
              transition: 'all 0.2s',
            }}
          >
            <FileJson size={36} color="#a855f7" style={{ marginBottom: 10 }} />
            <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>
              Drop a JSON file here, or click to browse
            </p>
            <p style={{ fontSize: 12, color: '#8885a0' }}>
              Accepts single-collection exports, full backups, or raw arrays
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              data-testid="import-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])}
              style={{ display: 'none' }}
            />
          </div>
        )}

        {importFile && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              background: 'rgba(0, 212, 255, 0.08)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              borderRadius: 10,
              padding: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                <FileText size={18} color="#00d4ff" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {importFile.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#8885a0' }}>
                    {isBundle
                      ? `Full bundle (${Object.keys(importPayload || {}).length} collections)`
                      : `Single: ${importEntity} (${Array.isArray(importPayload) ? importPayload.length : 1} records)`}
                  </div>
                </div>
              </div>
              <button
                data-testid="reset-import-btn"
                onClick={resetImport}
                style={{ ...ghostBtn, fontSize: 12 }}
              >
                Remove
              </button>
            </div>

            {/* Entity selector (only if not bundle) */}
            {!isBundle && (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Target Collection</label>
                <select
                  data-testid="import-entity-select"
                  value={importEntity}
                  onChange={(e) => setImportEntity(e.target.value)}
                  style={selectStyle}
                >
                  {ENTITIES.map((ent) => (
                    <option key={ent.key} value={ent.key}>{ent.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Mode selector */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Import Mode</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {MODES.map((m) => (
                  <label
                    key={m.value}
                    data-testid={`mode-${m.value}`}
                    style={{
                      display: 'flex',
                      gap: 12,
                      padding: 12,
                      borderRadius: 10,
                      border: `1px solid ${importMode === m.value ? '#a855f7' : 'rgba(139, 92, 246, 0.25)'}`,
                      background: importMode === m.value ? 'rgba(139, 92, 246, 0.12)' : 'rgba(139, 92, 246, 0.03)',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="radio"
                      name="mode"
                      value={m.value}
                      checked={importMode === m.value}
                      onChange={(e) => setImportMode(e.target.value)}
                      style={{ marginTop: 3, accentColor: '#a855f7' }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                        {m.label}
                        {m.value === 'replace' && (
                          <span style={{ fontSize: 10, color: '#ef4444', marginLeft: 8 }}>⚠ Destructive</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: '#8885a0' }}>{m.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                data-testid="preview-import-btn"
                onClick={handlePreview}
                disabled={loading}
                style={{ ...outlineBtn, flex: 1, justifyContent: 'center', borderColor: '#00d4ff', color: '#00d4ff' }}
              >
                {loading && !result ? <Loader2 size={14} className="animate-spin" /> : null} Preview
              </button>
              <button
                data-testid="apply-import-btn"
                onClick={handleApply}
                disabled={loading}
                style={{
                  ...primaryBtn,
                  flex: 1,
                  justifyContent: 'center',
                  background: importMode === 'replace'
                    ? 'linear-gradient(135deg, #dc2626, #ef4444)'
                    : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                }}
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Apply Import
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div data-testid="import-error" style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 8,
            padding: 12,
            marginTop: 14,
            display: 'flex',
            gap: 10,
            color: '#fca5a5',
            fontSize: 13,
          }}>
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {/* Preview result */}
        {preview && (
          <div data-testid="import-preview" style={resultBox('#00d4ff')}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileJson size={14} color="#00d4ff" /> Preview (nothing changed yet)
            </div>
            <pre style={preStyle}>{JSON.stringify(preview, null, 2)}</pre>
          </div>
        )}

        {/* Apply result */}
        {result && (
          <div data-testid="import-result" style={resultBox('#22c55e')}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle size={14} color="#22c55e" /> Import applied successfully
            </div>
            <pre style={preStyle}>{JSON.stringify(result, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

// ========== styles ==========
const cardStyle = {
  background: 'rgba(88, 28, 135, 0.08)',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  borderRadius: 14,
  padding: 22,
};
const sectionHeader = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: '1px solid rgba(139, 92, 246, 0.15)',
};
const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: 10,
};
const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  color: 'white',
  border: 'none',
  padding: '12px 20px',
  borderRadius: 10,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: 'Inter, sans-serif',
};
const outlineBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  background: 'rgba(139, 92, 246, 0.06)',
  border: '1px solid rgba(139, 92, 246, 0.3)',
  color: '#e0dff0',
  padding: '10px 14px',
  borderRadius: 10,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'Inter, sans-serif',
  transition: 'all 0.15s',
};
const ghostBtn = {
  background: 'transparent',
  border: '1px solid rgba(139, 92, 246, 0.3)',
  color: '#b0adc0',
  padding: '6px 12px',
  borderRadius: 8,
  cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
};
const labelStyle = {
  display: 'block',
  fontSize: 12,
  color: '#8885a0',
  marginBottom: 8,
  textTransform: 'uppercase',
  letterSpacing: 1,
};
const selectStyle = {
  width: '100%',
  background: 'rgba(15, 10, 30, 0.6)',
  border: '1px solid rgba(139, 92, 246, 0.3)',
  borderRadius: 8,
  padding: '10px 12px',
  color: '#e0dff0',
  fontSize: 14,
  fontFamily: 'Inter, sans-serif',
};
const resultBox = (color) => ({
  background: `${color}10`,
  border: `1px solid ${color}40`,
  borderRadius: 10,
  padding: 14,
  marginTop: 14,
});
const preStyle = {
  background: 'rgba(15, 10, 30, 0.8)',
  padding: 12,
  borderRadius: 8,
  fontSize: 12,
  color: '#b0adc0',
  overflow: 'auto',
  maxHeight: 300,
  fontFamily: 'monospace',
};

export default AdminDataManager;
